import { useMemo } from "react";
import { create } from "zustand";
import { type DraftRefusal, useSubmitAnswer } from "./useQueues";

// Review's queue of answer posts from the shared confirm card (0074): one per
// product for the app's lifetime, one post at a time, in tap order, outliving
// the card and the page. Every Review answer otherwise posts through its own
// card's useSubmitAnswer; a pass started from this browser waits for this
// queue (auth/useTriggerRun.ts).

export type QueuedAct = "yes" | "save";

// A row's post state, kept for the app's lifetime: the act whose post is
// queued or out (null when none), the answer its last successful post saved
// (undefined before any) and when (Date.now() at that success), and its last
// failure with the answer that post carried and when (Date.now() at that
// failure; cleared by its next tap, by its next success, or by clearFailure).
export type RowPost = {
  pending: QueuedAct | null;
  settled?: string;
  settledAt?: number;
  error?: unknown;
  failed?: string;
  failedAt?: number;
};

// One queue per product for the app's lifetime (the useGlobalRunStore
// pattern): each product's row states and the tail of its promise chain.
export const useAnswerQueueStore = create<{
  rows: Record<string, Record<string, RowPost>>;
  tails: Record<string, Promise<void>>;
}>(() => ({ rows: {}, tails: {} }));

// The rows of a product with none (one constant, so the store's selector never
// hands back a fresh object).
const EMPTY_ROWS: Record<string, RowPost> = {};

function patchRow(productId: string, questionId: string, patch: (row: RowPost) => RowPost): void {
  useAnswerQueueStore.setState((s) => {
    const rows = s.rows[productId] ?? EMPTY_ROWS;
    const row = rows[questionId] ?? { pending: null };
    return { rows: { ...s.rows, [productId]: { ...rows, [questionId]: patch(row) } } };
  });
}

// No failure: what a tap, a success or clearFailure leaves on a row.
const NO_FAILURE = { error: undefined, failed: undefined, failedAt: undefined };

// Drops a row's failure that a later read superseded (the shared card decides:
// a read no older than the failure serves the answer that post carried as
// saved, so the post committed though its response was lost), unless the row
// failed again since.
export function clearFailure(productId: string, questionId: string, failedAt: number): void {
  if (useAnswerQueueStore.getState().rows[productId]?.[questionId]?.failedAt !== failedAt) return;
  patchRow(productId, questionId, (row) => ({ ...row, ...NO_FAILURE }));
}

export function useAnswerQueue(
  productId: string,
  opts?: { onRefused?: (refusal: DraftRefusal) => void },
): {
  rows: ReadonlyMap<string, RowPost>;
  // Resolves true when this job's post succeeded; false when it failed or was ignored.
  enqueue: (job: { questionId: string; answer: string; selectedPick?: string; act: QueuedAct }) => Promise<boolean>;
} {
  // No item: a member's post carries no covered-by pick and is no decision's,
  // so it has no refusal an item line could name.
  const submit = useSubmitAnswer(productId, { onRefused: opts?.onRefused });
  const record = useAnswerQueueStore((s) => s.rows[productId] ?? EMPTY_ROWS);
  const rows = useMemo(() => new Map(Object.entries(record)), [record]);

  const enqueue = (job: { questionId: string; answer: string; selectedPick?: string; act: QueuedAct }) => {
    const { questionId, answer, selectedPick, act } = job;
    // A row whose act is queued or out takes no other: the job is ignored,
    // whichever mount queued the one pending.
    if (useAnswerQueueStore.getState().rows[productId]?.[questionId]?.pending) return Promise.resolve(false);
    // The tap is taken at once: the row is pending from here, and its last
    // failure is cleared.
    patchRow(productId, questionId, (row) => ({ ...row, pending: act, ...NO_FAILURE }));
    let settle: (ok: boolean) => void = () => {};
    const result = new Promise<boolean>((resolve) => {
      settle = resolve;
    });
    const before = useAnswerQueueStore.getState().tails[productId] ?? Promise.resolve();
    // Each job waits for the one before it and catches its own failure, so the
    // chain never rejects and a failure never stops a later post. The job's
    // promise settles just before its row's state moves, so a caller reads the
    // page as it was when the post landed and the store as it is after.
    const tail = before.then(async () => {
      try {
        await submit.mutateAsync({ questionId, answer, selectedPick });
      } catch (err) {
        settle(false);
        patchRow(productId, questionId, (row) => ({
          ...row,
          pending: null,
          error: err,
          failed: answer,
          failedAt: Date.now(),
        }));
        return;
      }
      settle(true);
      patchRow(productId, questionId, (row) => ({
        ...row,
        pending: null,
        settled: answer,
        settledAt: Date.now(),
        ...NO_FAILURE,
      }));
    });
    useAnswerQueueStore.setState((s) => ({ tails: { ...s.tails, [productId]: tail } }));
    return result;
  };

  return { rows, enqueue };
}
